// PUBLISHING a firmware build: put a .bin on the server, record it, and point a
// device type at it.
//
// firmwareInfo.js reads which build a device runs. firmwareFleet.js sends the
// `updatecode` word. Neither can change WHAT a device downloads — this can, and
// it is the only thing in the app that can. Ported from the C# desktop tool's
// `BtnDeviceTypeUploadBinFile_Click`, which is what has been doing this job.
//
// ── The three calls, in order ──────────────────────────────────────────────
//   1. POST <host>/upload            multipart, field `file`  -> { url }
//   2. POST /api/AddOTABinFile       upserts OTABinFile on BinFileName
//   3. POST /api/UpdateDeviceType    sets that row's id on a DeviceID (optional)
//
// ⚠️ STEP 1 IS THE DEPLOYMENT. The moment the file lands, every device whose
// row names it fetches the new bytes on its next `updatecode` — steps 2 and 3
// only update bookkeeping. So a failure at step 2 does NOT mean "nothing
// happened"; it means the firmware shipped and the record of it did not.
// `publishBinFile` reports the step it reached for exactly this reason.
//
// ── The upload endpoint is not under /api ──────────────────────────────────
// It is a sibling route on the same Express app: `/upload`, not `/api/upload`.
// Verified live — it answers `{"status":false,"message":"No file uploaded"}` to
// a bodyless POST and carries `Access-Control-Allow-Origin: *`, so the browser
// can reach it directly. multipart/form-data is a CORS-safelisted content type,
// so there is no preflight to satisfy either.
//
// ⚠️ THE DESTINATION FOLDER COMES FROM THE FILE EXTENSION, NOT FROM US. The
// handler is `path.extname(file.name).substring(1)` -> `/var/www/html/uploads/
// <ext>/`. So `X.bin` lands in `/uploads/bin/` — where the firmware looks — and
// `X.BIN` lands in `/uploads/BIN/`, a different directory on a case-sensitive
// filesystem, which nginx then serves as a 404 to every device that tries it.
// The C# file dialog filtered on `*.BIN` and got away with it only because
// Windows hands the real name through. `checkBinName` rejects it here.
//
// ── What a browser cannot do that the C# tool could ────────────────────────
// `HexFileLocalLocation` is the build's path on the machine that made it
// (`H:/Mix_Data/espressif/claude/OtaForSwitching/OtaForSwitching.ino.bin` on the
// current row). A file picked in a browser exposes its NAME and nothing else —
// there is no path, by design. So the field is typed in or left alone, and
// `publishBinFile` omits it entirely when blank rather than writing an empty
// string over a good value.

import axios from 'axios';
import {
  API_BASE, addOtaBinFile, getOtaBinFileByName, updateDeviceType,
  updateDeviceInstalled, getDeviceOtaUrl,
} from '../api/api';
import { SERVER_TZ_OFFSET_MS } from './deviceStatus';
import { saveHandle } from './buildHandles';

/**
 * Where the multipart POST goes.
 *
 * Derived from API_BASE by dropping the trailing `/api`, so a deployment that
 * moves the backend moves this with it and there is no second host to keep in
 * step. Overridable for the case where uploads are split onto their own box.
 */
export const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL
  || `${API_BASE.replace(/\/api\/?$/, '')}/upload`;

// The server writes the .bin under /uploads/<ext>/ and hands back the URL, so
// this is only used to say what to expect before the upload runs.
export const EXPECTED_BIN_DIR = '/uploads/bin/';

// ── What chip is this build actually for? ──────────────────────────────────
// The first 16 bytes of an ESP-IDF image answer it, and answering it BEFORE the
// upload is the only cheap defence against the one failure that reports itself
// as a success.
//
// ⚠️ A CROSS-CHIP UPDATE LOOKS LIKE IT WORKED. `CodeUpdated` is stamped by the
// device BEFORE ESPhttpUpdate runs (see firmwareInfo.js), so a device that
// rejects a wrong-architecture image at the bootloader's chip check still comes
// back reading "Up to date" on firmware it never took. Nothing downstream can
// catch that — the row says it updated. So it has to be caught here.
//
// Layout (esp_image_header_t): magic(1) segment_count(1) spi_mode(1)
// spi_speed/size(1) entry_addr(4) wp_pin(1) spi_pin_drv(3) chip_id(2 LE at 12)
// min_chip_rev(1).
//
// ⚠️ ESP8266 IMAGES ALSO START 0xE9 AND HAVE NO chip_id. Their header is 8
// bytes and offset 12 is already segment payload, so reading a chip id there
// returns garbage. Measured on the three builds in production — OtaForSwitching,
// OtaForSwitchingNewDesign and OtaForTuya all read `80 0d 00 00` at 12..15,
// i.e. 0x0d80 = 3456, which is not a chip id at all. They are also all
// `segment_count = 1` against the ESP32 builds' 5. So an unrecognised value is
// reported as "ESP8266-class, no chip id" rather than guessed at.
export const ESP_CHIPS = {
  0x0000: 'ESP32',
  0x0002: 'ESP32-S2',
  0x0005: 'ESP32-C3',
  0x0009: 'ESP32-S3',
  0x000C: 'ESP32-C2',
  0x000D: 'ESP32-C6',
  0x0010: 'ESP32-H2',
  0x0012: 'ESP32-P4',
};

// Module marketing names, because the FILE is usually named for the module and
// the HEADER is named for the die. ESP8684 is an ESP32-C2 part and ESP8685 is
// an ESP32-C3 one — one digit apart, incompatible images, and the mistake is
// invisible until a fleet fails to update.
export const MODULE_CHIPS = {
  esp8684: 'ESP32-C2',
  esp8685: 'ESP32-C3',
  esp8266: 'ESP8266',
  esp32c2: 'ESP32-C2',
  esp32c3: 'ESP32-C3',
  esp32c6: 'ESP32-C6',
  esp32s2: 'ESP32-S2',
  esp32s3: 'ESP32-S3',
};

export const ESP8266_CLASS = 'ESP8266-class';

// esp_app_desc_t lives at a fixed offset in an ESP-IDF application image and
// carries the version, project name, IDF version and build time — the same
// fields `esptool image_info` prints. Reading it means the upload can say WHICH
// build is being published, not just that the file is an image.
const APP_DESC_OFF = 0x20;
const APP_DESC_MAGIC = 0xABCD5432;
// ⚠️ MUST REACH 0xD0, NOT 0xB0. `app_elf_sha256` is the LAST field of the
// descriptor (offset 0x90 within it, i.e. 0xB0 in the file) and its first eight
// bytes are exactly what the device reports as `sha=` in its VERSION reply —
// verified against board E072A14582D0: the file's 17ad9f1cf7dc0e99 is what came
// back over MQTT. Stopping at 0xB0 reads everything EXCEPT the one field that
// can prove a device is running a given file.
const APP_DESC_END = 0xD0;

/**
 * Read the chip out of a picked .bin, without uploading it.
 *
 * -> { ok, size, segments, chip, chipId, minChipRev, error }
 *    `chip` is ESP8266_CLASS when the header carries no usable chip id.
 *
 * `ok:false` means it is not an ESP image at all (no 0xE9), which is worth
 * blocking: it is almost always the wrong file rather than an exotic build.
 */
export async function readEspImage(file) {
  const b = new Uint8Array(await file.slice(0, APP_DESC_END).arrayBuffer());
  if (b.length < 16) return { ok: false, error: 'The file is too small to be a firmware image.', size: file.size };
  if (b[0] !== 0xE9) {
    return {
      ok: false,
      size: file.size,
      error: `This is not an ESP firmware image — it starts 0x${b[0].toString(16).padStart(2, '0')}, not 0xE9.`,
    };
  }
  const chipId = b[12] | (b[13] << 8);
  const known = Object.prototype.hasOwnProperty.call(ESP_CHIPS, chipId);

  // The app descriptor. Present in an ESP-IDF APPLICATION and absent from the
  // build's other artefacts, which is the only way to tell them apart — they
  // are all valid ESP images with the right chip id.
  // ⚠️ DETECTED ON THE MAGIC ALONE, NOT ON HAVING THE WHOLE DESCRIPTOR. Gating
  // this on the full 0xD0 bytes means a short read reports "no app descriptor",
  // which BLOCKS the upload — turning a truncated read into a refusal to
  // publish a perfectly good image. Each field below is guarded by its own
  // length instead, so a partial read gives fewer facts rather than a wrong
  // verdict.
  const desc = b.length >= APP_DESC_OFF + 4
    && ((b[APP_DESC_OFF] | (b[APP_DESC_OFF + 1] << 8)
      | (b[APP_DESC_OFF + 2] << 16) | (b[APP_DESC_OFF + 3] << 24)) >>> 0) === APP_DESC_MAGIC;

  const hex = (off, len) => {
    let h = '';
    for (let i = off; i < off + len && i < b.length; i += 1) h += b[i].toString(16).padStart(2, '0');
    return h;
  };

  const str = (off, len) => {
    let s = '';
    for (let i = off; i < off + len && i < b.length && b[i]; i += 1) s += String.fromCharCode(b[i]);
    return s;
  };

  return {
    ok: true,
    size: file.size,
    segments: b[1],
    chipId: known ? chipId : null,
    chip: known ? ESP_CHIPS[chipId] : ESP8266_CLASS,
    minChipRev: known ? b[14] : null,
    isApp: desc,
    appVersion: desc ? str(0x30, 32) : '',
    projectName: desc ? str(0x50, 32) : '',
    builtAt: desc ? `${str(0x80, 16)} ${str(0x70, 16)}`.trim() : '',
    idfVersion: desc ? str(0x90, 32) : '',
    // Full hash for the record; `sha8` is the 16 hex chars the device reports.
    elfSha256: desc && b.length >= 0xD0 ? hex(0xB0, 32) : '',
    sha8: desc && b.length >= 0xB8 ? hex(0xB0, 8) : '',
  };
}

/**
 * Is this one of the build's OTHER artefacts rather than the app?
 *
 * -> the refusal sentence, or '' if it looks like a real app image.
 *
 * ⚠️ `bootloader.bin` is a valid ESP image with the RIGHT chip id — the chip
 * check alone waves it straight through, and it is sitting in the same build
 * folder as the file you actually want. OTA_CLAUDE.md §3 names the whole trap:
 * "Not bootloader.bin, not partition-table.bin, not ota_data_initial.bin, not
 * the .elf. Those are flashing-jig artifacts; serving one as an OTA image fails
 * at the header check."
 *
 * Only ESP32-family images are judged. An ESP8266 build has no app descriptor
 * either, and those are real OTA images for the installed fleet — so "no
 * descriptor" is only evidence when the header carried a chip id.
 */
export function notAnAppImage(image) {
  if (!image || !image.ok || !image.chipId) return '';
  if (image.isApp) return '';
  return 'This is not an application image — it has no ESP-IDF app descriptor.'
    + ' It is most likely bootloader.bin, partition-table.bin or ota_data_initial.bin'
    + ' from the same build folder. The device would reject it at the header check.';
}

/**
 * Does the FILENAME claim a different chip than the image is built for?
 *
 * -> the mismatch sentence, or '' when they agree or the name says nothing.
 *
 * This exists because it already happened: `ESP8684WROOM04C.bin` — an ESP8684,
 * i.e. ESP32-C2 module — was built as an ESP32-C3 image in a `build_c3` folder.
 * The name is the identity here (it keys the row AND is what every mapped
 * device fetches), so a wrong one is not a cosmetic problem: correcting it later
 * means a new row plus re-pointing every device already on the old one.
 */
export function chipNameMismatch(fileName, chip) {
  if (!chip || chip === ESP8266_CLASS) return '';
  const flat = String(fileName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const hit = Object.keys(MODULE_CHIPS).find((k) => flat.includes(k));
  if (!hit) return '';
  const claimed = MODULE_CHIPS[hit];
  if (claimed === chip) return '';
  return `The name says ${hit.toUpperCase()} (${claimed}) but the image is built for ${chip}.`
    + ' Rename it before uploading — the file name is what every device asks for,'
    + ' so changing it later means a new row and re-pointing every device on it.';
}

// Steps, in the order publishBinFile performs them. Reported through onStep and
// carried on a thrown error as `.step`, so a caller can say WHICH half failed.
export const STEP_UPLOAD = 'upload';
export const STEP_RECORD = 'record';
export const STEP_RESOLVE = 'resolve';
export const STEP_LINK = 'link';

/**
 * A timestamp in the format the server's own rows are in.
 *
 * ⚠️ NOT toISOString(). The backend writes IST wall-clock as a naive string and
 * the JSON serialiser then appends a `Z` — the fault deviceStatus.js documents
 * and firmwareInfo.parseServerTime undoes by subtracting SERVER_TZ_OFFSET_MS.
 * A row stamped with real UTC would come back out 5½ hours EARLY, which reads
 * as a build older than the devices already running it: every device on a fresh
 * upload would report "Needs update" and stay that way.
 *
 * The C# tool got this right by accident — `DateTime.Now` on an IST machine is
 * IST. Adding the offset and formatting the UTC fields gets the same string
 * from any timezone, which matters now that this runs on the operator's phone.
 */
export function serverStamp(nowMs = Date.now()) {
  return new Date(nowMs + SERVER_TZ_OFFSET_MS).toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * Is this filename safe to upload?
 *
 * -> { ok, error, warning }
 *
 * `error` blocks; `warning` is shown and can be accepted. The distinction is
 * whether the device would still find the file: a wrong extension puts it in a
 * folder nothing reads (fatal), a space in the name merely makes the URL ugly
 * and needs encoding (survivable, but worth seeing).
 */
export function checkBinName(name) {
  const n = String(name || '');
  if (!n) return { ok: false, error: 'No file chosen.' };
  if (!n.endsWith('.bin')) {
    return {
      ok: false,
      error: n.toLowerCase().endsWith('.bin')
        ? `The extension must be lower-case .bin — "${n.slice(n.lastIndexOf('.'))}" would be`
          + ` stored under /uploads/${n.slice(n.lastIndexOf('.') + 1)}/ instead of`
          + ` ${EXPECTED_BIN_DIR}, and every device asking for it would get a 404.`
        : 'A firmware file must end in .bin.',
    };
  }
  if (/\s/.test(n)) {
    return { ok: true, warning: 'The name contains a space. The URL will work but has to be encoded — renaming the build is cleaner.' };
  }
  if (/[^A-Za-z0-9._-]/.test(n)) {
    return { ok: true, warning: 'The name contains characters that have to be URL-encoded. Renaming the build is cleaner.' };
  }
  return { ok: true };
}

/**
 * Step 1 — put the file on the server.
 *
 * -> the public URL the server reports, e.g.
 *    http://smartifyindia.co.in/uploads/bin/OtaForSwitching.ino.bin
 *
 * ⚠️ DO NOT SET Content-Type. The boundary is generated by the browser and a
 * hand-written header loses it, at which point express-fileupload sees no files
 * and answers `{status:false}` with HTTP 200 — a silent failure that looks like
 * a successful upload. That is why the reply is checked for a url rather than
 * for a status code.
 */
export async function uploadBinFile(file, { onProgress, signal } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);

  const res = await axios.post(UPLOAD_URL, form, {
    signal,
    timeout: 120000,          // a bin is ~400 KB, but site uplinks are not
    onUploadProgress: (e) => {
      if (!onProgress) return;
      onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null);
    },
  });

  const url = res && res.data && (res.data.url || res.data.Url);
  if (!url) {
    const msg = (res && res.data && res.data.message) || 'The server accepted the request but returned no URL.';
    throw new Error(`Upload failed: ${msg}`);
  }
  return String(url);
}

/**
 * The full OTABinFile row for a filename, or null.
 *
 * The list endpoint returns two columns; this is the only way to read UartPins
 * and HexFileLocalLocation, which is what a re-publish needs so it can carry
 * them forward instead of blanking them.
 */
export async function getBinFileRow(binFileName) {
  const res = await getOtaBinFileByName(binFileName);
  const row = Array.isArray(res.data) ? res.data[0] : null;
  return row || null;
}

/**
 * Step 3 — what OTABinFileId did the upsert actually land on?
 *
 * ⚠️ THIS IS THE ONE BUG IN THE C# NOT WORTH PORTING. It linked the device type
 * to `CbOTABinFile.SelectedValue` — whatever was showing in the combo box —
 * rather than to the row it had just written. Upload a new build, and the type
 * gets pointed at the PREVIOUS file.
 *
 * The id cannot be read from AddOTABinFile's reply either: it inserts on a new
 * name and updates on a known one, so the reply is two different shapes and
 * only one of them carries an id. Reading it back by name covers both.
 */
export async function resolveBinFileId(binFileName) {
  const row = await getBinFileRow(binFileName);
  const id = row && row.OTABinFileId;
  return id == null ? null : Number(id);
}

// ── Where the build came from on THIS machine ──────────────────────────────
// `HexFileLocalLocation` is the one column a browser genuinely cannot fill in:
// a picked file exposes its NAME and nothing else, by design, and no permission
// or plugin changes that on the web.
//
// So the folder is remembered instead. Typed once per computer, it is joined to
// the picked filename to reconstruct exactly what the C# tool recorded
// (`E:/visualstudioprojects/arduino code/OTA/OtaForSwitching.ino.bin`) without
// asking for the whole path every time. It is a convenience, not a source of
// truth — the field stays editable, because a build that came out of a
// different folder must not be recorded as if it came out of this one.
const LOCAL_FOLDER_KEY = 'sli.ota.localFolder';

// ── Remembering which build a name currently holds ─────────────────────────
// `VERSION` gives the running image's ELF sha; the picked FILE gives the same
// sha for the build being published. Comparing them answers "is this device
// running this file" — which no timestamp can, and which is the only way to
// tell a silent rollback from a device that was already on that build.
//
// ⚠️ IT HAS TO BE REMEMBERED AT UPLOAD TIME, because it cannot be re-read
// later: the sha lives in the .bin, and the file host sends no CORS headers, so
// the browser is blocked from range-reading the published file. (firmwareInfo.js
// documents the same wall for Last-Modified.)
//
// ⚠️ PER-BROWSER, AND THEREFORE ADVISORY. There is no column on OTABinFile to
// put it in, so this only knows about builds published from this machine. An
// unknown sha means "cannot say", never "wrong" — the UI must not report a
// device as bad because a different operator did the upload.
const BUILD_SHA_KEY = 'sli.ota.buildSha';

const shaMap = () => {
  try {
    return JSON.parse(window.localStorage.getItem(BUILD_SHA_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
};

export function rememberBuildSha(binFileName, sha8) {
  if (!binFileName || !sha8) return;
  try {
    window.localStorage.setItem(BUILD_SHA_KEY, JSON.stringify({ ...shaMap(), [binFileName]: sha8 }));
  } catch (e) { /* private mode — the comparison is simply unavailable */ }
}

/** -> the sha8 this browser published under that name, or '' if unknown. */
export const knownBuildSha = (binFileName) => shaMap()[binFileName] || '';

export function getLocalFolder() {
  try {
    return window.localStorage.getItem(LOCAL_FOLDER_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function setLocalFolder(folder) {
  try {
    window.localStorage.setItem(LOCAL_FOLDER_KEY, String(folder || ''));
  } catch (e) { /* private mode — the field still works, it just won't persist */ }
}

/**
 * folder + filename, in the shape the column already holds.
 *
 * Backslashes are normalised to forward slashes because that is what the rows
 * contain — the C# did `temp_word.Replace("\\", "/")` for the same reason, and
 * a backslash in a value that also travels through JSON is one escape away from
 * corrupting itself.
 */
// ── Opening the picker where the build actually lives ──────────────────────
//
// ⚠️ NO WEB PAGE CAN POINT A FILE DIALOG AT A PATH, and this is not a gap to be
// worked around — it is the boundary that makes `<input type=file>` safe. The
// element has no such option at all, and the File System Access API's `startIn`
// deliberately accepts only a directory HANDLE the user already granted, or one
// of six well-known names ('desktop', 'documents', …). Never a string. A page
// that could open the dialog already sitting in C:/Users/x/.ssh would be one
// mis-click away from reading it, so `HexFileLocalLocation` cannot be handed to
// the picker however much we would like to.
//
// What IS reachable is the browser's own memory of where you last were.
// `showOpenFilePicker({ id })` remembers a directory per id and reopens there,
// so keying the id to the BIN FILE NAME gives every build its own folder from
// the second pick onward: OtaForTuya reopens in OtaForTuya's folder,
// OtaForSwitching in its own. The first pick still has to be navigated once.
//
// ⚠️ SECURE CONTEXT ONLY — so it is absent on the deployed plain-HTTP site,
// which is exactly what killed the Web Bluetooth path (see core/ble/bleClient.js
// before rebuilding anything like this). It is therefore a progressive
// enhancement over the hidden <input>, never a replacement: callers check
// `supportsFolderMemory()` and fall back.

/** Can this browser reopen the picker where it was last used? */
export const supportsFolderMemory = () =>
  typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';

// The spec restricts `id` to a short token, and silently ignores one it does
// not like — which would look like the memory simply not working.
const pickerId = (binFileName) => `sli-bin-${String(binFileName || 'default')
  .replace(/[^a-zA-Z0-9_-]/g, '-')
  .slice(0, 24)}`;

/**
 * Open the system picker, reopening the folder last used for THIS bin file.
 *
 * -> the File, or null when the operator cancelled.
 *
 * ⚠️ `startIn` IS DELIBERATELY NOT PASSED. Where the two options disagree the
 * remembered `id` is meant to win, but that ordering is not worth betting the
 * feature on — a hint that overrode the memory would send the operator back to
 * Documents on every single pick, which is the fault being fixed.
 */
export async function pickBinFile(binFileName) {
  const [handle] = await window.showOpenFilePicker({
    id: pickerId(binFileName),
    multiple: false,
    types: [{
      description: 'Firmware image',
      accept: { 'application/octet-stream': ['.bin'] },
    }],
  });
  if (!handle) return null;
  const file = await handle.getFile();
  // ⚠️ KEYED ON THE FILE THAT WAS PICKED, not on the bin row that was showing.
  // A pick is allowed to REPLACE which row the type points at, so keying on
  // `binFileName` would file this handle under the previous build and the next
  // one-click upload would send the wrong image. `file.name` is what the row
  // will be keyed on once it saves, so it is what the handle is keyed on now.
  await saveHandle(file.name, handle);
  return file;
}

export function joinLocalPath(folder, fileName) {
  const f = String(folder || '').trim().replace(/\\/g, '/').replace(/\/+$/, '');
  const n = String(fileName || '').trim();
  if (!n) return f;
  if (!f) return n;
  return `${f}/${n}`;
}

/**
 * Steps 2 and 3 — record the build and hand back the id it landed on.
 *
 * ⚠️ `stampedAt` IS DELIBERATELY OPTIONAL AND MUST STAY THAT WAY. Passing it
 * rewrites `HexFileDateTime`, which is the build date every device is compared
 * against — so re-stamping it when nothing was uploaded marks the whole fleet
 * stale for a build they are already running. Correcting a typo in the local
 * path is not a new build. Pass a stamp only when new bytes actually landed.
 *
 * -> the OTABinFileId of the row, or null if it could not be read back.
 */
export async function writeBinFileRow({
  binFileName, hexFileUrl = '', localPath = '', uartPins = '', stampedAt = null,
}) {
  const name = String(binFileName || '').trim();
  if (!name) throw new Error('A bin file name is required.');

  // Only non-empty values are sent. This endpoint writes whatever keys it is
  // handed straight onto the row, so an empty string is an ERASURE, not a
  // no-op — and these columns are shared by every device type on the file.
  const row = { BinFileName: name };
  if (hexFileUrl) row.HexFileUrl = hexFileUrl;
  if (localPath) row.HexFileLocalLocation = localPath;
  if (uartPins) row.UartPins = uartPins;
  if (stampedAt) row.HexFileDateTime = stampedAt;

  await addOtaBinFile(row);
  return resolveBinFileId(name);
}

/**
 * Step 4 — point a device type at a bin row.
 *
 * `stampedAt` is the DeviceType's own `OtaUpdateDateTime`, and is optional for
 * the same reason as above: it records when the type was re-pointed, not when
 * the file was built.
 */
export async function linkDeviceType(deviceId, otaBinFileId, stampedAt = null) {
  const data = { OTABinFileId: Number(otaBinFileId) };
  if (stampedAt) data.OtaUpdateDateTime = stampedAt;
  await updateDeviceType(data, `DeviceID = ${Number(deviceId)}`);
}

// ── Per-DEVICE firmware, which is the only way to run two chips ────────────
// `DevicesInstalled.OTABinFileId` OVERRIDES its type's, and GetDeviceOTAUrlLink
// honours it. Verified live: 935809 is a DeviceID 49 whose type points at
// OtaForSwitchingNewDesign.ino.bin, and it is served OtaForTuya.ino.bin.
//
// That override is the answer to OTA_CLAUDE.md §12.6 — "one GetDeviceOTAUrlLink
// cannot serve two chips from one value". It can, per device, and already does.
//
// ⚠️ WHY THIS CANNOT BE DRIVEN OFF A LIST. The boards that need it most have
// `LocationID = 0` and no owner — E072A14582D0, the C3 bench unit, is exactly
// that — so they appear in no location query, and the Tuya bench is
// time-windowed and routinely returns nothing. A unique number typed in by hand
// is the only handle that always works.

/**
 * Read what a device will ACTUALLY download, the way the device reads it.
 *
 * The firmware takes the first string-valued field of the first record whatever
 * it is named (OTA_CLAUDE.md §10), so this does the same rather than looking
 * for `OTAUpdateLink` — if the backend renames the column, the app keeps
 * agreeing with the hardware instead of quietly showing a blank.
 */
export async function servedOtaUrl(uniqueNumber) {
  const res = await getDeviceOtaUrl(uniqueNumber);
  const row = Array.isArray(res.data) ? res.data[0] : res.data;
  if (!row || typeof row !== 'object') return '';
  const first = Object.values(row).find((v) => typeof v === 'string' && v.trim());
  return first ? first.trim() : '';
}

/**
 * Point ONE device at a bin file, then prove it took.
 *
 * -> { url, ok } where `url` is what GetDeviceOTAUrlLink now returns and `ok`
 *    is whether it is the file that was asked for.
 *
 * The read-back is the point. A write that reports success while the device is
 * still served the old image is precisely the failure this whole area is prone
 * to, and it costs one request to rule out.
 */
export async function mapDeviceToBinFile(uniqueNumber, otaBinFileId, expectBinFileName) {
  await updateDeviceInstalled(uniqueNumber, { OTABinFileId: Number(otaBinFileId) });
  const url = await servedOtaUrl(uniqueNumber);
  const want = String(expectBinFileName || '').trim();
  return { url, ok: !!want && url.endsWith(`/${want}`) };
}

/**
 * Upload a build, record it, and optionally point a device type at it.
 *
 * @param file        the File from the picker
 * @param uartPins    OTABinFile.UartPins, e.g. "D7D8" (blank is allowed and
 *                    common — half the rows have none)
 * @param localPath   OTABinFile.HexFileLocalLocation. Omitted from the write
 *                    when blank, so re-uploading from a browser does not wipe a
 *                    path the desktop tool recorded.
 * @param deviceId    DeviceType.DeviceID to link, or null to record only
 * @param onStep      (step) => void, one of the STEP_* constants
 * @param onProgress  (percent|null) => void, during the upload only
 *
 * -> { url, binFileName, otaBinFileId, linkedDeviceId, stampedAt }
 *
 * On failure the thrown Error carries `.step` and `.url` (set once the file is
 * up), so the caller can distinguish "nothing shipped" from "the firmware
 * shipped but the database does not know".
 */
export async function publishBinFile({
  file,
  uartPins = '',
  localPath = '',
  deviceId = null,
  onStep,
  onProgress,
  signal,
} = {}) {
  const check = checkBinName(file && file.name);
  if (!check.ok) throw new Error(check.error);

  const binFileName = file.name;
  const stampedAt = serverStamp();
  const step = (s) => { if (onStep) onStep(s); };

  step(STEP_UPLOAD);
  const url = await uploadBinFile(file, { onProgress, signal });

  // Everything past here has the file already live. Tag the error with the URL
  // so the UI can show what shipped even when the bookkeeping fell over.
  const tag = (err, s) => {
    const e = err instanceof Error ? err : new Error(String(err));
    e.step = s;
    e.url = url;
    return e;
  };

  // New bytes DID land, so this is the one case where re-stamping the build
  // date is correct.
  step(STEP_RECORD);
  let otaBinFileId = null;
  try {
    otaBinFileId = await writeBinFileRow({
      binFileName, hexFileUrl: url, localPath, uartPins, stampedAt,
    });
  } catch (err) {
    throw tag(err, STEP_RECORD);
  }

  let linkedDeviceId = null;
  if (deviceId != null && deviceId !== '') {
    if (otaBinFileId == null) {
      throw tag(new Error('The bin file was recorded but could not be read back, so no device type was linked.'), STEP_RESOLVE);
    }
    step(STEP_LINK);
    try {
      await linkDeviceType(deviceId, otaBinFileId, stampedAt);
      linkedDeviceId = Number(deviceId);
    } catch (err) {
      throw tag(err, STEP_LINK);
    }
  }

  return { url, binFileName, otaBinFileId, linkedDeviceId, stampedAt };
}

export default publishBinFile;
